import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/apiServices';

const UserContext = createContext();

// In-memory storage for user profile (not persisted)
let userProfileCache = null;
let sessionTimeout = null;

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to get user profile from in-memory cache
  const getUserProfile = () => {
    return userProfileCache;
  };

  // Function to refresh user profile in in-memory cache
  const refreshUserProfile = (profileData) => {
    if (profileData) {
      userProfileCache = profileData; // Store in memory only
      setUser(profileData);
    } else {
      userProfileCache = null; // Clear from memory
      setUser(null);
    }
  };

  // Function to check if user has a specific role
  const hasRole = (roleName) => {
    if (!user || !user.roles) return false;
    if (typeof roleName === 'string') {
      return user.roles.includes(roleName);
    }
    if (Array.isArray(roleName)) {
      return roleName.some(role => user.roles.includes(role));
    }
    return false;
  };

  // Function to check if user has a specific permission
  const hasPermission = (permissionName) => {
    if (!user || !user.permissions) return false;
    if (typeof permissionName === 'string') {
      return user.permissions.includes(permissionName);
    }
    if (Array.isArray(permissionName)) {
      return permissionName.some(permission => user.permissions.includes(permission));
    }
    return false;
  };

  // Function to check if user has any of the specified permissions
  const hasAnyPermission = (permissionNames) => {
    if (!Array.isArray(permissionNames)) return false;
    return permissionNames.some(permissionName => hasPermission(permissionName));
  };

  // Function to check if user has all of the specified permissions
  const hasAllPermissions = (permissionNames) => {
    if (!Array.isArray(permissionNames)) return false;
    return permissionNames.every(permissionName => hasPermission(permissionName));
  };

  // Function to logout and clear user data
  const logout = React.useCallback(() => {
    setUser(null);
    userProfileCache = null; // Clear from memory
    localStorage.removeItem('userProfile'); // Clear from localStorage
    if (sessionTimeout) clearTimeout(sessionTimeout);
    authService.logout();
  }, []);

  // Function to start session timeout
  const startSessionTimeout = React.useCallback(() => {
    // Clear any existing timeout
    if (sessionTimeout) clearTimeout(sessionTimeout);

    // Set new timeout (e.g., 30 minutes of inactivity)
    sessionTimeout = setTimeout(() => {
      logout();
    }, 30 * 60 * 1000); // 30 minutes
  }, [logout]);

  // Function to reset session timeout
  const resetSessionTimeout = React.useCallback(() => {
    if (sessionTimeout) clearTimeout(sessionTimeout);
    startSessionTimeout();
  }, [startSessionTimeout]);

  // Effect to initialize user state
  useEffect(() => {
    setLoading(true);
    // Get user profile from in-memory cache only
    const profile = getUserProfile();
    setUser(profile);
    setLoading(false);
  }, []);

  // Effect to set up activity listeners for session timeout
  useEffect(() => {
    if (!user) return; // Only set up listeners if user is logged in

    const events = ['mousedown', 'keydown', 'scroll', 'touchmove', 'click'];
    const resetTimeout = resetSessionTimeout;

    events.forEach(event => window.addEventListener(event, resetTimeout));

    // Start the initial timeout
    startSessionTimeout();

    return () => {
      events.forEach(event => window.removeEventListener(event, resetTimeout));
      if (sessionTimeout) clearTimeout(sessionTimeout);
    };
  }, [user, resetSessionTimeout, startSessionTimeout]);

  const value = {
    user,
    loading,
    refreshUserProfile,
    hasRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    logout
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};